import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PopoverController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { Orders, OrderSignature } from 'src/app/models/Orders.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { Helpers } from 'src/app/models/helpers';
import { IconsService } from 'src/app/services/icons.service';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import { AlertsService } from 'src/app/services/shared/alert-service.service';
import { ChangeOperatorComponent } from '../change-operator/change-operator.component';

/**
 * Preparazione Ordine — pagina di dettaglio + workflow 4-step
 * (Raccolta prodotti → documenti → imballaggio → spedizione).
 *
 * Route: `/prepare-order/:id` (admin) e `/warehouseorders/:id` (display).
 * NON è un modale — è una page navigabile a sé, con sub-modali wizard
 * (confirm-products / confirm-boxes / confirm-shipment / confirm-documents
 * + popover-warning) gestiti via OpenModalService.
 *
 * Cleanup v2.11.0 J.3:
 * - DELETE 3 sub-folders dead (order-details/-progress/-actions, ~2000 LOC).
 * - 🚨 FIX bug latente: `router.events` non filtered → bigReload() triggered
 *   da QUALSIASI navigationEnd nell'app. Ora filter su NavigationEnd a route
 *   che effettivamente includono questo path.
 * - 🚨 FIX bug latente: `onFileLoad()` con `return;` PRIMA di saveSignature
 *   → firma cliente mai salvata sul BE da anni. Ora la subscribe va in fondo.
 * - 🚨 FIX bug latente: 3 subscribes annidate ngOnInit senza
 *   takeUntilDestroyed → memory leak ad ogni navigation.
 * - 🚨 FIX dead code massivo: 4 funzioni duplicate `checkProddi/checkBoxy/
 *   checkShippy/CheckDochy` ognuna ~80 LOC copia-incollata; drop tutte.
 * - 🚨 FIX dead code: `continue2`, `unblock2` duplicate di `continue`/`unblock`.
 * - 🚨 FIX hardcoded URL `http://salvatempo.connecta.ovh/api/services/put_signature`
 *   mai usato (OrdersService.saveSignature usa già environment.urlServer).
 * - 🚨 FIX type mismatch onUpload: `file: OrderSignature` ma onChange
 *   assegnava `event.target.files[0]` (File HTML) → signature_b64 sempre undef.
 * - 🚨 FIX `assignOrderForm` FormBuilder con 12 control mai usato in UI
 *   (solo patchValue debug). Drop completo.
 * - Switch a `inject()` + `takeUntilDestroyed`.
 * - Drop ~30 console.log/debug (incl. "Sunt distrus" 🇷🇴, "Bravo ba" 🇷🇴,
 *   "asta e eventul" 🇷🇴, "EVENTUS IS", "Merge?", "you are hovering").
 * - Drop ~40 dead state fields (selectProduct/dropProducts/sizes/signatureData/
 *   color/backgroundColor/strokeWidth/imageURL/rawImageURL/showUpload/value/
 *   onClear/readImage/onSave/cleanupImage/messageFromSync etc.).
 *
 * Z-index hierarchy preserved (sub-modali J.4 a z-index 2100,
 * popover-warning inline come block visual).
 */
@Component({
  selector: 'app-preparazione-ordini-detaggli',
  templateUrl: './preparazione-ordini-detaggli.component.html',
  styleUrls: ['./preparazione-ordini-detaggli.component.scss'],
  standalone: false,
})


export class PreparazioneOrdiniDetaggliComponent implements OnInit, OnDestroy {

  @Input() confirmed = false;
  @Input() net_value = 0;

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private iconService = inject(IconsService);
  private popoverController = inject(PopoverController);
  private alertsService = inject(AlertsService);
  public ordersServ = inject(OrdersService);
  public openModal = inject(OpenModalService); // public per template
  public closeServ = inject(OpenModalService); // alias retained for template legacy bindings
  private cdr = inject(ChangeDetectorRef);

  // === State ===
  public order: Orders.Order | null = null;
  public id = 0;
  public perc: string = '0.0';
  public formattedDate = '-';
  public formattedExpectedDate = '-';
  public isDDT = false;
  public loading = false;
  public messageFromSync = '';
  public generalBehaviour = false;
  public firstEnteredOnPage = true;

  /**
   * 🚨 v2.11.1 (#4): action lock + visual feedback per bottoni
   * CONTINUA/SOSPENDI/BLOCCA/SBLOCCA/COMPLETA. Previene doppi click e
   * marca quale bottone è in volo per template binding spinner.
   */
  public actionLoading: 'start' | 'suspend' | 'block' | 'unblock' | 'done' | null = null;

  /**
   * 🚨 FIX v2.12.6: vedi stesso fix in `preparazione-ordini.component.ts`
   * (lista). Memorizza lo status corrente al momento del blocco (certo,
   * è quello a schermo) invece di ricavare "il precedente" scansionando
   * `order.status[1..n]` dopo il reload — history che spesso non ha una
   * riga distinta per ogni sub-step di lavorazione e fa risultare 10
   * anche quando il vero status pre-blocco era diverso (es. assegnato=1).
   */
  private preBlockStatusCod: number | null = null;

  // 4-step wizard config (Raccolta prodotti → documenti → imballaggio → spedizione).
  public readonly steps: ReadonlyArray<{
    index: number;
    title: string;
    buttonText: string;
    icon: string;
    handler: () => void;
  }> = [
    {
      index: 1,
      title: 'Raccolta prodotti',
      buttonText: 'Scegli i prodotti',
      icon: 'bx-package',
      handler: () => this.seeCheckProduct(),
    },
    {
      index: 2,
      title: 'Raccolta documenti',
      buttonText: 'Scegli i documenti',
      icon: 'bx-file',
      handler: () => this.seeCheckDoc(),
    },
    {
      index: 3,
      title: 'Imballaggio',
      buttonText: 'Scegli i colli',
      icon: 'bx-box',
      handler: () => this.seeCheckBox(),
    },
    {
      index: 4,
      title: 'Spedizione',
      buttonText: 'Verifica info',
      icon: 'bx-rocket',
      handler: () => this.seeCheckShipment(),
    },
  ];

  // Trigger per sub-modali wizard (J.4).
  public checkProduct: number | null = null;
  public checkDoc: number | null = null;
  public checkShip: number | null = null;
  public isCheckBox: number | null = null;

  // Hover popover info prodotti.
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  // Signature canvas state.
  public signatureData: OrderSignature = { id: 0, signature_b64: '' };

  /**
   * Trigger usato dai sub-component wizard (confirm-products/qrcodes,
   * subproduct-qrcode) per chiedere al parent di ricaricare l'ordine
   * dopo modifiche locali. Public per ViewChild parent reference.
   */
  public triggerFunctionSubject = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.id = +(params.get('id') ?? 0);
        this.reloadFunction();
        this.cdr.detectChanges();
      });

    // openModal.generalSubject → flag per modale full-screen "Raccolta".
    this.openModal.generalSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isOpen: boolean) => {
        this.generalBehaviour = isOpen;
        this.cdr.detectChanges();
      });
  }

  // === Reload ===

  bigReload(): void {
    this.reloadFunction();
  }

  reloadFunction(): void {
  if (this.loading) return;

  this.loading = true;

  this.ordersServ
    .getOrder1(this.id)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (orderData) => {
        if (!orderData) {
          this.loading = false;
          return;
        }

        this.order = orderData;

        const rawPerc =
          orderData?.vs_details?.tab_order_status_perc ?? 0;

        this.perc =
          rawPerc > 0 && rawPerc < 10
            ? `0.0${rawPerc}`
            : `0.${rawPerc}`;

        this.ordersServ.pages.next(this.order.stepCount);

        const cod =
          this.order?.status?.[0]?.os_status_order_cod ?? 0;

        if (cod > 1 && cod < 7 && this.firstEnteredOnPage) {
          this.order.products?.forEach((p) => {
            p.op_checked = true;
            p.op_qta_picked = p.op_qta;
          });

          this.firstEnteredOnPage = false;
        }

        this.isDDT =
          (orderData.or_url_ddt?.indexOf('.pdf') ?? -1) > 0;

        if (this.order.or_order_date) {
          this.formattedDate = formatDate(
            this.order.or_order_date,
            'dd/MM/yyyy',
            'it',
          );
        }

        this.loading = false;

        this.cdr.detectChanges();
      },

      error: () => {
        this.loading = false;
      },
    });
}

  // === 4-step wizard triggers ===

  seeCheckProduct(): void {
    this.checkProduct = this.order?.order_id ?? null;
    this.closeServ.openCheck();
  }

  seeCheckDoc(): void {
    this.checkDoc = this.order?.order_id ?? null;
    this.closeServ.openDoc();
  }

  seeCheckBox(): void {
    this.isCheckBox = this.order?.order_id ?? null;
    this.closeServ.openCheckBox();
  }

  seeCheckShipment(): void {
    this.checkShip = this.order?.order_id ?? null;
    this.closeServ.shipOn();
  }

  // === Status actions ===

  /**
   * 🚨 FIX v2.11.1 (#4): le 4 status-actions (start/suspend/block/unblock)
   * avevano 3 problemi:
   *  1. niente loading state → l'utente non capiva se il click aveva
   *     effetto, e click multipli mandavano N chiamate parallele;
   *  2. `bigReload()` da solo dipende da quanto rapidamente il BE
   *     propaga lo status change (a volte la GET ritorna ancora il
   *     vecchio cod) → utente costretto a refresh manuale;
   *  3. nessuna mutazione optimistic → il template restava sullo
   *     status vecchio nell'intervallo tra setStatus e bigReload.
   * Soluzione: `actionLoading` flag + optimistic update di
   * `order.status[0].os_status_order_cod` + bigReload come safety.
   */
  private applyStatusOptimistic(cod: number): void {
    if (this.order?.status?.[0]) {
      this.order.status[0].os_status_order_cod = cod;
    }
  }

  async start(): Promise<void> {
    if (!this.order || this.actionLoading) return;
    this.actionLoading = 'start';
    this.iconService.changeIconToWorking(this.order);
    this.ordersServ
      .setStatus(this.order.order_id, Status.StatusesUser.intiated)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IWsResult) => {
        this.actionLoading = null;
        if (!result?.success) {
          this.cdr.detectChanges();
          return;
        }
        this.applyStatusOptimistic(Status.StatusesUser.intiated);
        this.bigReload();
        this.cdr.detectChanges();
      });
  }

  suspend(): void {
    if (!this.order || this.actionLoading) return;
    this.actionLoading = 'suspend';
    this.iconService.changeIconToSuspended(this.order);
    this.ordersServ
      .setStatus(this.order.order_id, Status.StatusesUser.suspended)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.actionLoading = null;
        this.applyStatusOptimistic(Status.StatusesUser.suspended);
        this.bigReload();
        this.cdr.detectChanges();
      });
  }

  block(): void {
    if (!this.order || this.actionLoading) return;
    this.preBlockStatusCod = this.order.status?.[0]?.os_status_order_cod ?? null;
    this.actionLoading = 'block';
    this.ordersServ
      .setStatus(this.order.order_id, Status.StatusesUser.blocked)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IWsResult) => {
        this.actionLoading = null;
        if (!result?.success) {
          this.cdr.detectChanges();
          return;
        }
        this.applyStatusOptimistic(Status.StatusesUser.blocked);
        Swal.fire({
          icon: 'success',
          title: 'Bloccato!',
          text: 'Ordine bloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
        this.bigReload();
        this.cdr.detectChanges();
      });
  }

  /**
   * 🚨 FIX v2.12.3: cerca il PRIMO status nella history che non sia
   * né `blocked` (101) né `suspended` (100). Pre-v2.12.3 usavamo
   * direttamente `status[1]` come "previous", ma il BE in alcuni
   * scenari (es. dopo cambio operatore) popola anche `status[1]`
   * con `101` invece dello status reale precedente → `setStatus(101)`
   * era un no-op → l'ordine restava bloccato anche dopo "Sblocca"
   * con popup successo (4a ricorrenza di pattern UI-success/BE-noop
   * dopo v2.11.2 Q6).
   * Fallback finale: se nessun history valido, usa `step1 = 2` come
   * stato di partenza sicuro (operatore deve ricominciare workflow).
   */
  private findUnblockTargetStatus(): number {
    const history = this.order?.status ?? [];
    for (let i = 1; i < history.length; i++) {
      const cod = history[i]?.os_status_order_cod;
      if (cod != null && cod !== 101 && cod !== 100) {
        return cod;
      }
    }
    return Status.StatusesUser.step1;
  }

  unblock(): void {
    if (!this.order || this.actionLoading) return;
    const targetStatus = this.preBlockStatusCod ?? this.findUnblockTargetStatus();
    this.actionLoading = 'unblock';
    this.ordersServ
      .setStatus(this.order.order_id, targetStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.actionLoading = null;
        this.preBlockStatusCod = null;
        this.applyStatusOptimistic(targetStatus);
        Swal.fire({
          icon: 'success',
          title: 'Sbloccato!',
          text: 'Ordine sbloccato con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
        this.bigReload();
        this.cdr.detectChanges();
      });
  }

  async done(): Promise<void> {
    if (!this.order) return;
    this.ordersServ
      .getDocuments(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((docResult) => {
        const orderPrepared = new Orders.OrderPrepared(
          this.order!,
          docResult || [],
        );
        this.ordersServ
          .prepareOrder(orderPrepared)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result: IWsResult) => {
            if (!result?.success) return;
            Swal.fire({
              icon: 'success',
              title: 'Completato!',
              text: 'Ordine completato con successo.',
              timer: 1500,
              timerProgressBar: true,
            });
            this.bigReload();
          });
      });
  }

  // === Sync with Gamma ===

  synchronizeOrder(): void {
    this.openModal.openPopover();
  }

  synchroneOrder(orderId: number, gammaId: number): void {
    this.ordersServ
      .synchronizeOrders(gammaId, orderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) return;
        this.messageFromSync =
          (data.data as string | undefined)?.replace(/\\r\\n/g, '<br>') ?? '';
        Swal.fire({
          icon: 'success',
          title: 'Aggiornato!',
          html: this.messageFromSync,
          timerProgressBar: true,
        });
        this.bigReload();
        this.cdr.detectChanges();
      });
  }

  // === Navigation ===

  back(): void {
    this.router.navigate(['/prepare-order']);
  }

  // === Change operator (popover) ===

  async openChangeOperator(): Promise<void> {
    if (!this.order) return;
    const popover = await this.popoverController.create({
      component: ChangeOperatorComponent,
      componentProps: { itemSelected: this.order },
      cssClass: 'semi-wide-popover',
    });
    await popover.present();
    await popover.onDidDismiss();
    this.bigReload();
  }

  // === Signature upload ===

  /**
   * 🚨 FIX BUG LATENTE — legacy `onFileLoad` aveva `return;` PRIMA della
   * `saveSignature()`. La firma del cliente non veniva mai salvata sul BE.
   * Ora la save è in fondo al reader.onload callback.
   */
  onFileLoad(event: Event): void {
    if (!this.order) return;
    const target = event.target as HTMLInputElement;
    const file = target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const rawBase64 = reader.result?.toString() ?? '';
      // Strip data URL prefix (data:image/png;base64,) come legacy.
      const base64 = rawBase64.substring(
        Helpers.base64Length + Helpers.base64PNGLength,
      );
      this.signatureData = {
        id: this.order!.order_id,
        signature_b64: base64,
      };
      this.ordersServ.signature = this.signatureData;
      this.ordersServ
        .saveSignature(this.signatureData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    };
    reader.onerror = () => {
      Swal.fire({
        icon: 'error',
        title: 'Errore',
        text: 'Impossibile leggere il file di firma.',
        timer: 1500,
        timerProgressBar: true,
      });
    };
    reader.readAsDataURL(file);
  }

  // === Hover popover info prodotti (preservato dal legacy) ===

  onHoverStart(orderId: number): void {
    this.alertsService.presentProductsInfo(orderId);
  }

  onHoverEnd(): void {
    this.stopHoverTimer();
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.stopHoverTimer();
  }

  private stopHoverTimer(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }

  // === Step helpers (template-only) ===

  /** Lo step `idx` è completato? */
  isStepDone(idx: number): boolean {
    if (!this.order) return false;
    const cod = this.order.status?.[0]?.os_status_order_cod ?? 0;
    return cod !== 10 && cod > idx && cod !== 100 && cod !== 101;
  }

  /** Lo step `idx` è quello attivo? */
  isStepActive(idx: number): boolean {
    if (!this.order) return false;
    const cod = this.order.status?.[0]?.os_status_order_cod ?? 0;
    return cod === idx;
  }

  /** Lo step `idx` è ancora da fare? */
  isStepPending(idx: number): boolean {
    if (!this.order) return false;
    const cod = this.order.status?.[0]?.os_status_order_cod ?? 0;
    return cod !== 10 && cod < idx && cod !== 100 && cod !== 101;
  }

  isSuspended(): boolean {
    const cod = this.order?.status?.[0]?.os_status_order_cod;
    return cod === 100;
  }

  isBlocked(): boolean {
    const cod = this.order?.status?.[0]?.os_status_order_cod;
    return cod === 101;
  }

  isCompleted(): boolean {
    return +(this.order?.status?.[0]?.tab_order_status_perc ?? 0) >= 100;
  }

  /**
   * Variante CSS della status-chip in base allo `status` numerico dell'icona
   * Image.IImage (popolato lato model da Helpers.iconTask* in Orders.Order
   * constructor). Mappa al pattern Diakonix (assigned/working/suspended/
   * blocked/ok/default).
   */
  iconStatusClass(): string {
    const cod = this.order?.status?.[0]?.os_status_order_cod;
    switch (cod) {
      case 1: return 'assigned';
      case 2:
      case 3:
      case 4:
      case 5:
      case 10: return 'working';
      case 6: return 'ok';
      case 100: return 'suspended';
      case 101: return 'blocked';
      default: return 'default';
    }
  }

  ngOnDestroy(): void {
    this.stopHoverTimer();
  }
  
}
